import { useToast } from '@chakra-ui/react'
import { captureException } from '@sentry/react'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import Copy01Icon from '../../../../../assets/icons/copy-01-stroke-rounded'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import Toast from '../../../../../components/Toast'
import TransferStatusBadge from '../../../../../components/TransferStatusBadge'
import { useGetSwapsQuery } from '../../../../../redux/services/coreApi'
import { setTransfersTableState } from '../../../../../redux/slices/tableFiltersSlice'
import { RootState } from '../../../../../redux/store'
import { SwapDto } from '../../../../../types/coreApi-types'
import downloadSwapInvoice from '../../../../../utils/downloadInvoice'
import { formatNumber } from '../../../../../utils/formatters'
import TransactionSummaryModal from './components/TransactionSummaryModal'

type BookkeeperTransfersScreenProps = {}

const BookkeeperTransfersScreen: React.FC<
  BookkeeperTransfersScreenProps
> = ({}) => {
  const toast = useToast()
  const sessionToken = useSelector((state: RootState) => state.session.token)

  const { data: swapsData, isLoading } = useGetSwapsQuery(null)

  const [activeSwap, setActiveSwap] = useState<SwapDto | null>(null)
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false)

  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState<{
    [swapId: string]: boolean
  }>({})

  const openSummaryModal = (v: SwapDto) => {
    setActiveSwap(v)
    setIsSummaryModalOpen(true)
  }

  const transfersTableState = useSelector(
    (state: RootState) => state.transfersTable
  )

  return (
    <PageWrapper title="Swap Invoices">
      <Table
        tableState={transfersTableState}
        setTableState={setTransfersTableState}
        navigate={() => null}
        selectMenuActions={[
          {
            title: 'Download invoices',
            icon: 'download',
            selected: values => values,
            onClick: async rows => {
              const typedRows = rows as { original: SwapDto }[]
              await Promise.all(
                typedRows.map(async row => {
                  const swap = row.original
                  if (swap.swapStatus === 'Completed') {
                    try {
                      await downloadSwapInvoice(swap, sessionToken)
                    } catch (e) {
                      captureException(e)
                    }
                  }
                })
              )
              return rows
            },
            hidden: _values => false,
          },
        ]}
        isLoading={isLoading}
        onClickRow={v => {
          openSummaryModal(v)
        }}
        emptyDataRender={
          <div className="text-xl mb-3">No swap invoices yet.</div>
        }
        rowMenuActions={[
          {
            title: 'View invoice details',
            onClick: v => {
              openSummaryModal(v)
            },
            hidden: v => false,
          },
          {
            title: 'Download receipt',
            onClick: async v => {
              setIsDownloadingInvoice(prev => ({
                ...prev,
                [v.id]: true,
              }))

              try {
                await downloadSwapInvoice(v, sessionToken)
              } catch (e) {
                captureException(e)
              } finally {
                setIsDownloadingInvoice(prev => ({
                  ...prev,
                  [v.id]: false,
                }))
              }
            },
            isLoading: v => isDownloadingInvoice[v.id],
            disabled: v => isDownloadingInvoice[v.id],
            hidden: v => v.swapStatus !== 'Completed',
          },
        ]}
        defaultData={[
          ...(swapsData?.map(item => {
            return {
              ...item,
              name: item.user.name,
              status: item.swapStatus,
              bankDepositUniqueId: item.deposit?.bankDepositUniqueId,
              depositAmount: formatNumber(
                item.deposit!.amount,
                item.deposit!.currency
              ),
              withdrawAmount: formatNumber(
                item.withdraw!.amount,
                item.withdraw!.currency
              ),
            }
          }) ?? []),
        ]}
        schema={[
          {
            header: 'Status',
            accessorKey: 'status',
            type: 'status',
            priority: 1,
            cellValueWrapper: (value, originalValue, { original }) => (
              <div className="w-fit min-w-[120px]">
                {original?.status && (
                  <TransferStatusBadge transferStatus={original.status} />
                )}
              </div>
            ),
          },
          {
            cardTitle: true,
            header: 'Date created',
            accessorKey: 'createdAt',
            type: 'date',
            priority: 2,
            initialSorted: true,
          },
          {
            header: 'Investor name',
            accessorKey: 'name',
            type: 'string',
            priority: 3,
          },
          {
            header: 'Deposit',
            accessorKey: 'depositAmount',
            type: 'string',
            priority: 4,
          },
          {
            header: 'Receive',
            accessorKey: 'withdrawAmount',
            type: 'string',
            priority: 5,
          },
          {
            header: 'Bank Deposit Id',
            accessorKey: 'bankDepositUniqueId',
            type: 'string',
            priority: 6,
            cellValueWrapper: (value, originalValue, { original }) =>
              original?.bankDepositUniqueId ? (
                <div
                  className="px-2 py-1.5 w-min hover:bg-gray-100 transition-all rounded-md group"
                  onClick={e => {
                    e.stopPropagation()
                    navigator.clipboard.writeText(original.bankDepositUniqueId!)

                    toast({
                      position: 'top',
                      render: () => {
                        return <Toast type="success">Id copied</Toast>
                      },
                    })
                  }}
                >
                  <div className="flex gap-1.5 items-center">
                    {original.bankDepositUniqueId}
                    <div className="group-hover:opacity-100 opacity-0 transition-all">
                      <Copy01Icon className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ) : (
                <></>
              ),
          },
          {
            header: 'Admin note',
            accessorKey: 'adminNote',
            type: 'string',
            priority: 7,
          },
        ]}
      />
      <TransactionSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        activeSwap={activeSwap}
        withdrawTransactionHash=""
        setWithdrawTransactionHash={() => {}}
        setSwapStatus={async () => {
          // Read-only mode - no status updates allowed, return current swap
          if (!activeSwap) {
            throw new Error('No active swap to return')
          }
          return activeSwap
        }}
        setSwapsStatusIsLoading={false}
        adminNote=""
        setAdminNote={() => {}}
        setTempTransferStatus={() => {}}
        openConfirmModal={() => {}}
        isReadOnly={true}
      />
    </PageWrapper>
  )
}

export default BookkeeperTransfersScreen
